import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard, EmailGuard } from "../App";
import { FaMoneyBillWave, FaUser, FaDollarSign, FaPen, FaTimes, FaSpinner, FaSyncAlt } from 'react-icons/fa';
import { Oval } from "react-loader-spinner"; // Assuming this is available
import { StandardURL } from "../App";

// Helper to format currency (Ksh)
const formatCurrency = (amount) => {
    const value = parseFloat(amount);
    return isNaN(value) ? 'Ksh 0.00' : `Ksh ${value.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const DepositPage = () => {
    const [deposits, setDeposits] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDeposit, setSelectedDeposit] = useState(null);
    const [updateAmount, setUpdateAmount] = useState("");
    const [isFetching, setIsFetching] = useState(true); // Initial fetch loading state
    const [isUpdating, setIsUpdating] = useState(false); // Modal submission loading state

    useEffect(() => {
        fetchDeposits();
    }, []);

    const fetchDeposits = () => {
        setIsFetching(true);
        const token = localStorage.getItem("token");
        axios
            .get(`${StandardURL}/iNyumba/money/viewDeposit`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
            .then((response) => {
                // Ensure response.data.response is an array before setting
                if (Array.isArray(response.data.response)) {
                    setDeposits(response.data.response);
                } else {
                    setDeposits([]);
                }
            })
            .catch((error) => {
                console.error("Error fetching deposits:", error);
                toast.error(error.response?.data?.response || "Failed to fetch deposits.");
                setDeposits([]);
            })
            .finally(() => {
                setIsFetching(false);
            });
    };

    const handleUpdateModalOpen = (deposit) => {
        setSelectedDeposit(deposit);
        // Ensure to use the correct key from the fetched object (deposit.deposit in your original code)
        setUpdateAmount(deposit.deposit || deposit.amount || ""); 
        setModalOpen(true);
    };

    const handleUpdateDeposit = () => {
        if (!selectedDeposit || isNaN(parseFloat(updateAmount)) || parseFloat(updateAmount) < 0) {
            toast.error("Please enter a valid, non-negative amount.");
            return;
        }

        setIsUpdating(true);
        const token = localStorage.getItem("token");
        
        axios
            .patch(
                `${StandardURL}/iNyumba/money/updatedeposit`,
                {
                    // Assuming the API expects user_id from the deposit object
                    userid: selectedDeposit.user_id,
                    amount: updateAmount,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            )
            .then((response) => {
                toast.success(response.data.response || "Deposit updated successfully!");
                setModalOpen(false);
                setIsUpdating(false);
                fetchDeposits(); // Refresh the list
            })
            .catch((error) => {
                console.error("Error updating deposit:", error);
                toast.error(error.response?.data?.response || "Failed to update deposit.");
                setIsUpdating(false);
            });
    };

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
                    <div className="max-w-4xl mx-auto">
                        {/* Header */}
                        <div className="flex justify-between items-center mb-6">
                            <h1 className="text-3xl font-extrabold text-gray-900 flex items-center">
                                <FaMoneyBillWave className="mr-3 text-green-600" /> Manage Deposits
                            </h1>
                            <button
                                onClick={fetchDeposits}
                                disabled={isFetching}
                                className="inline-flex items-center bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg transition duration-150 shadow-md disabled:bg-indigo-400"
                            >
                                <FaSyncAlt className={`mr-2 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
                            </button>
                        </div>

                        {/* Deposits Table Card */}
                        <div className="bg-white shadow-xl rounded-xl overflow-hidden border-t-4 border-green-500">
                            
                            {isFetching ? (
                                <div className="flex flex-col items-center justify-center p-12">
                                    <Oval color="#10b981" secondaryColor="#34d399" height={50} width={50} ariaLabel="loading-spinner" />
                                    <p className="mt-4 text-gray-600">Loading deposits...</p>
                                </div>
                            ) : deposits.length === 0 ? (
                                <div className="text-center p-12">
                                    <FaMoneyBillWave className="mx-auto h-12 w-12 text-gray-400" />
                                    <h3 className="mt-2 text-lg font-medium text-gray-900">No Deposits Found</h3>
                                    <p className="mt-1 text-sm text-gray-500">
                                        There are no deposits recorded. Try refreshing the data.
                                    </p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    <FaUser className="inline mr-1" /> User ID
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    <FaDollarSign className="inline mr-1" /> Amount
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {deposits.map((deposit) => (
                                                <tr key={deposit.user_id} className="hover:bg-gray-50 transition duration-100">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        {deposit.user_id}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-semibold">
                                                        {/* Assuming deposit is the amount field, check both possible keys */}
                                                        {formatCurrency(deposit.deposit || deposit.amount)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                        <button
                                                            onClick={() => handleUpdateModalOpen(deposit)}
                                                            className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-1.5 px-3 rounded-md transition duration-150 shadow-sm text-xs"
                                                        >
                                                            <FaPen className="mr-1" /> Update
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Update Deposit Modal */}
                {modalOpen && selectedDeposit && (
                    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            {/* Background overlay */}
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setModalOpen(false)}></div>

                            {/* Modal panel */}
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                            
                            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <FaDollarSign className="h-6 w-6 text-blue-600" />
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                            <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                                Update Deposit Amount
                                            </h3>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500 mb-2">
                                                    Updating deposit for **User ID: {selectedDeposit.user_id}**.
                                                </p>
                                                <label htmlFor="updateAmount" className="block text-sm font-medium text-gray-700">New Amount (Ksh)</label>
                                                <input
                                                    type="number"
                                                    id="updateAmount"
                                                    value={updateAmount}
                                                    onChange={(e) => setUpdateAmount(e.target.value)}
                                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                    placeholder="Enter new deposit amount"
                                                    min="0"
                                                    step="any"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                                    <button
                                        onClick={handleUpdateDeposit}
                                        disabled={isUpdating || isNaN(parseFloat(updateAmount)) || parseFloat(updateAmount) < 0}
                                        className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm transition duration-150 ${
                                            isUpdating ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        }`}
                                    >
                                        {isUpdating ? (
                                            <Oval height={20} width={20} color="white" visible={true} ariaLabel="loading-indicator" secondaryColor="#a5b4fc" />
                                        ) : (
                                            <>
                                                <FaPen className="mr-2 mt-0.5" /> Update Deposit
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setModalOpen(false)}
                                        type="button"
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:w-auto sm:text-sm transition duration-150"
                                    >
                                        <FaTimes className="mr-2 mt-0.5" /> Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </AuthGuard>
        </EmailGuard>
    );
};

export default DepositPage;